import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showPartNumberLabel?: boolean;
    showMFGLabel?: boolean;
    showCustomerNumberLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: {
        gap: 5,
        css: css`
            margin-bottom: 10px;
        `,
    },
    erpNumberGridItem: {
        width: [4, 4, 8, 4, 3],
    },
    manufacturerItemGridItem: {
        width: [8, 8, 12, 6, 6],
    },
    erpNumberLabelText: {
        weight: "bold",
        size: "16px",
        color: "#8a8a8a",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        color: "#8a8a8a",
        size: "16px",
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        color: "#8a8a8a",
        size: "16px",
        css: css`
            flex: 0 0 auto;
            margin-right: 4px;
        `,
    },
    manufacturerItemValueText: {
        color: "#8a8a8a",
        size: "16px",
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
};
const ProductDetailsPartNumber: React.FC<Props> = ({ product }) => {
    if (!product) {
        return null;
    }
    const styles = productPartNumbersStyles;
    const numberOfItems = 2;
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container} data-test-selector="GridContainer">
            <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                    {product.productNumber}
                </Typography>
            </GridItem>
            <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #:")}</Typography>
                <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                    {product.manufacturerItem}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPartNumber),
    definition: {
        displayName: "Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
